import { snackBarConfig } from 'src/app/app.constants';
import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, switchMap, map, filter, take } from 'rxjs/operators';

import { AuthService } from './../services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class TokenInterceptor implements HttpInterceptor {
  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<
    string | null
  >(null);

  constructor(
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private translateService: TranslateService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.authService.getToken() && req.url.indexOf('/oauth/token') === -1) {
      req = this.addTokenToRequest(req, this.authService.getToken());
    }

    return next.handle(req).pipe(
      catchError(error => {
        if (error.status === 401 && req.url.indexOf('/oauth/token') === -1) {
          return this.handle401Error(req, next);
        }
        if (req.url.indexOf('/oauth/token') === -1) {
          this.handleError(error);
        }
        return throwError(error);
      })
    ) as any;
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any> | any> {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.tokenSubject.next(null);
      if (!this.authService.isTokenExpired('refresh_token')) {
        return this.authService.refreshToken().pipe(
          switchMap(res => {
            this.isRefreshingToken = false;
            this.authService.setSession(res.access_token, res.refresh_token);
            this.tokenSubject.next(this.authService.getToken());
            return next.handle(
              this.addTokenToRequest(request, this.authService.getToken())
            );
          }),
          catchError(err => {
            this.isRefreshingToken = false;
            this.authService.logout();
            return throwError(err);
          })
        );
      } else {
        this.isRefreshingToken = false;
        this.authService.logout();
        return null;
      }
    } else {
      return this.tokenSubject.asObservable().pipe(
        filter(val => val != null),
        take(1),
        switchMap(val => {
          this.isRefreshingToken = false;
          return next.handle(
            this.addTokenToRequest(request, this.authService.getToken())
          );
        })
      );
    }
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string): any {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private handleError(errorReponse: HttpErrorResponse): void {
    this.translateService
      .get('error.' + errorReponse.status)
      .subscribe((statusCode: string) => {
        this.snackBar.open(
          errorReponse.error.error_description,
          statusCode,
          snackBarConfig
        );
      });
  }
}
